/**
 * WebGPU Renderer for Slang Sandbox
 */

import { TextureManager } from './texture-manager.js';
import { PASS_IDS } from './pass-manager.js';

// Constants
const MAX_DEVICE_PIXEL_RATIO = 2;
const UNIFORM_BUFFER_SIZE = 256; // bytes: 32 system + 224 custom
const SYSTEM_UNIFORM_SIZE = 32; // bytes for system uniforms (resolution, time, mouse)
const FPS_UPDATE_INTERVAL = 1000; // ms
const BUFFER_FORMAT = 'rgba16float'; // High precision for feedback effects

// Fullscreen triangle vertex shader (WGSL)
const VERTEX_SHADER = `
struct VertexOutput {
  @builtin(position) position: vec4f,
  @location(0) uv: vec2f,
}

@vertex
fn main(@builtin(vertex_index) vertexIndex: u32) -> VertexOutput {
  // Fullscreen triangle
  var pos = array<vec2f, 3>(
    vec2f(-1.0, -1.0),
    vec2f(3.0, -1.0),
    vec2f(-1.0, 3.0)
  );
  var uv = array<vec2f, 3>(
    vec2f(0.0, 1.0),
    vec2f(2.0, 1.0),
    vec2f(0.0, -1.0)
  );

  var output: VertexOutput;
  output.position = vec4f(pos[vertexIndex], 0.0, 1.0);
  output.uv = uv[vertexIndex];
  return output;
}
`;

// Default fragment shader (used when no shader is loaded)
const DEFAULT_FRAGMENT_SHADER = `
struct Uniforms {
  resolution: vec2f,
  time: f32,
  _padding: f32,
  mouse: vec2f,
}

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var iChannel0: texture_2d<f32>;
@group(0) @binding(2) var iSampler: sampler;
@group(0) @binding(3) var iChannel1: texture_2d<f32>;
@group(0) @binding(4) var iChannel2: texture_2d<f32>;
@group(0) @binding(5) var iChannel3: texture_2d<f32>;
@group(0) @binding(6) var iBufferA: texture_2d<f32>;
@group(0) @binding(7) var iBufferB: texture_2d<f32>;
@group(0) @binding(8) var iBufferC: texture_2d<f32>;
@group(0) @binding(9) var iBufferD: texture_2d<f32>;

@fragment
fn main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let color = vec3f(uv.x, uv.y, 0.5 + 0.5 * sin(uniforms.time));
  return vec4f(color, 1.0);
}
`;

export class WebGPURenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.device = null;
    this.context = null;
    this.format = null;
    this.pipeline = null;
    this.uniformBuffer = null;
    this.uniformBindGroup = null;
    this.bindGroupLayout = null;

    // Texture support
    this.textureManager = null;

    // Custom uniforms manager reference
    this.uniformManager = null;

    // Multi-pass buffer support
    // Map of bufferId -> { current: GPUTexture, currentView: GPUTextureView, previous: GPUTexture, previousView: GPUTextureView }
    this.bufferTextures = new Map();
    // Map of passId -> GPURenderPipeline
    this.bufferPipelines = new Map();
    // Map of passId -> GPUBindGroup
    this.bufferBindGroups = new Map();
    // List of enabled buffer IDs in execution order
    this.enabledBuffers = [];
    // Flag to indicate bind groups need rebuilding
    this.bindGroupsDirty = true;
    // Flag to indicate device has been lost
    this.deviceLost = false;
    // Resize debounce request ID
    this.resizeRequestId = null;

    // Uniforms data
    this.uniforms = {
      resolution: [0, 0],
      time: 0,
      mouse: [0, 0],
    };

    // Current fragment shader code (for rebinding)
    this.currentFragmentShader = null;

    // Animation state
    this.startTime = performance.now();
    this.animationId = null;
    this.isRunning = false;

    // Pause state
    this.isPaused = false;
    this.pausedTime = 0; // time value when paused
    this.pauseStartTime = 0; // performance.now() when paused

    // FPS tracking
    this.frameCount = 0;
    this.lastFpsUpdate = 0;
    this.fps = 0;
    this.onFpsUpdate = null;

    // Error callback
    this.onError = null;

    // Frame callback (for recording)
    this.onFrameCallback = null;
  }

  /**
   * Check if WebGPU is supported
   * @returns {boolean}
   */
  static isSupported() {
    return !!navigator.gpu;
  }

  /**
   * Initialize WebGPU
   * @returns {Promise<boolean>}
   */
  async init() {
    if (!WebGPURenderer.isSupported()) {
      this.reportError('WebGPU is not supported in this browser.');
      return false;
    }

    try {
      // Request adapter
      const adapter = await navigator.gpu.requestAdapter();
      if (!adapter) {
        this.reportError('Failed to get GPU adapter.');
        return false;
      }

      // Request device
      this.device = await adapter.requestDevice();
      this.device.lost.then((info) => {
        console.error('WebGPU device lost:', info.message);
        this.deviceLost = true;
        this.stop();
        this.reportError(`GPU device lost: ${info.message}`);
      });

      // Configure canvas context
      this.context = this.canvas.getContext('webgpu');
      if (!this.context) {
        this.reportError('Failed to get WebGPU context.');
        return false;
      }

      this.format = navigator.gpu.getPreferredCanvasFormat();
      this.context.configure({
        device: this.device,
        format: this.format,
        alphaMode: 'premultiplied',
      });

      // Create uniform buffer
      this.uniformBuffer = this.device.createBuffer({
        size: UNIFORM_BUFFER_SIZE,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });

      // Create texture manager
      this.textureManager = new TextureManager(this.device);
      this.textureManager.init();
      this.textureManager.onTextureChange = () => this.rebuildBindGroup();

      // Set initial canvas size
      this.resize();

      // Create default pipeline
      await this.createPipeline(DEFAULT_FRAGMENT_SHADER);

      // Setup resize observer with debounce
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeRequestId) {
          cancelAnimationFrame(this.resizeRequestId);
        }
        this.resizeRequestId = requestAnimationFrame(() => {
          this.resizeRequestId = null;
          this.resize();
        });
      });
      this.resizeObserver.observe(this.canvas.parentElement);

      // Setup mouse tracking
      this.setupMouseTracking();

      return true;
    } catch (error) {
      this.reportError(`WebGPU initialization failed: ${error.message}`);
      return false;
    }
  }

  /**
   * Setup mouse position tracking
   */
  setupMouseTracking() {
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.uniforms.mouse[0] = e.clientX - rect.left;
      this.uniforms.mouse[1] = rect.height - (e.clientY - rect.top); // Flip Y
    });
  }

  /**
   * Handle canvas resize
   */
  resize() {
    const parent = this.canvas.parentElement;
    const dpr = Math.min(window.devicePixelRatio, MAX_DEVICE_PIXEL_RATIO);
    const width = Math.floor(parent.clientWidth * dpr);
    const height = Math.floor(parent.clientHeight * dpr);

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.width = `${parent.clientWidth}px`;
      this.canvas.style.height = `${parent.clientHeight}px`;
      this.uniforms.resolution = [width, height];

      // Recreate buffer textures at new size
      if (this.enabledBuffers.length > 0) {
        this.recreateBufferTextures();
      }
    }
  }

  /**
   * Find fragment entry point name from WGSL code
   * @param {string} wgslCode
   * @returns {string|null}
   */
  findFragmentEntryPoint(wgslCode) {
    // Match @fragment followed by fn <name>
    const match = wgslCode.match(/@fragment\s*\n?\s*fn\s+(\w+)/);
    return match ? match[1] : null;
  }

  /**
   * Create render pipeline with fragment shader
   * @param {string} fragmentShaderCode - WGSL fragment shader code
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async createPipeline(fragmentShaderCode) {
    if (!this.device) {
      return { success: false, error: 'Device not initialized' };
    }

    try {
      // Find fragment entry point name
      const fragEntryPoint = this.findFragmentEntryPoint(fragmentShaderCode);
      if (!fragEntryPoint) {
        return { success: false, error: 'No @fragment entry point found in shader' };
      }

      // Create shader modules
      const vertexModule = this.device.createShaderModule({
        label: 'Vertex Shader',
        code: VERTEX_SHADER,
      });

      const fragmentModule = this.device.createShaderModule({
        label: 'Fragment Shader',
        code: fragmentShaderCode,
      });

      // Check for compilation errors
      const vertexInfo = await vertexModule.getCompilationInfo();
      const fragmentInfo = await fragmentModule.getCompilationInfo();

      const errors = [
        ...vertexInfo.messages.filter((m) => m.type === 'error'),
        ...fragmentInfo.messages.filter((m) => m.type === 'error'),
      ];

      if (errors.length > 0) {
        const errorMsg = errors
          .map((e) => `Line ${e.lineNum}: ${e.message}`)
          .join('\n');
        return { success: false, error: `Shader compilation error:\n${errorMsg}` };
      }

      // Create bind group layout with texture support (4 channels + 4 buffers)
      this.bindGroupLayout = this.device.createBindGroupLayout({
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.FRAGMENT,
            buffer: { type: 'uniform' },
          },
          {
            binding: 1,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
          {
            binding: 2,
            visibility: GPUShaderStage.FRAGMENT,
            sampler: { type: 'filtering' },
          },
          {
            binding: 3,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
          {
            binding: 4,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
          {
            binding: 5,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
          // Buffer textures (binding 6-9)
          {
            binding: 6,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
          {
            binding: 7,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
          {
            binding: 8,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
          {
            binding: 9,
            visibility: GPUShaderStage.FRAGMENT,
            texture: { sampleType: 'float' },
          },
        ],
      });

      // Create pipeline layout
      const pipelineLayout = this.device.createPipelineLayout({
        bindGroupLayouts: [this.bindGroupLayout],
      });

      // Create render pipeline
      this.pipeline = this.device.createRenderPipeline({
        label: 'Render Pipeline',
        layout: pipelineLayout,
        vertex: {
          module: vertexModule,
          entryPoint: 'main',
        },
        fragment: {
          module: fragmentModule,
          entryPoint: fragEntryPoint,
          targets: [{ format: this.format }],
        },
        primitive: {
          topology: 'triangle-list',
        },
      });

      // Store current shader for rebinding
      this.currentFragmentShader = fragmentShaderCode;

      // Create bind group with textures (4 channels + 4 buffers)
      this.uniformBindGroup = this.createMainBindGroup();

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Set uniform manager reference
   * @param {import('./uniform-manager.js').UniformManager} manager
   */
  setUniformManager(manager) {
    this.uniformManager = manager;
  }

  /**
   * Update uniform buffer
   */
  updateUniforms() {
    // System uniforms (8 floats = 32 bytes)
    const systemData = new Float32Array([
      this.uniforms.resolution[0],
      this.uniforms.resolution[1],
      this.uniforms.time,
      0, // padding
      this.uniforms.mouse[0],
      this.uniforms.mouse[1],
      0, // padding
      0, // padding
    ]);
    this.device.queue.writeBuffer(this.uniformBuffer, 0, systemData);

    // Custom uniforms (starting at offset 32)
    if (this.uniformManager && this.uniformManager.hasUniforms()) {
      const customData = this.uniformManager.toFloat32Array();
      if (customData.length > 0) {
        this.device.queue.writeBuffer(this.uniformBuffer, SYSTEM_UNIFORM_SIZE, customData);
      }
    }
  }

  /**
   * Update FPS counter and notify callback
   */
  updateFps() {
    this.frameCount++;
    const now = performance.now();
    if (now - this.lastFpsUpdate >= FPS_UPDATE_INTERVAL) {
      this.fps = Math.round((this.frameCount * 1000) / (now - this.lastFpsUpdate));
      this.frameCount = 0;
      this.lastFpsUpdate = now;
      if (this.onFpsUpdate) {
        this.onFpsUpdate(this.fps);
      }
    }
  }

  /**
   * Call frame callback and update FPS (common post-render logic)
   */
  postRender() {
    if (this.onFrameCallback) {
      this.onFrameCallback();
    }
    this.updateFps();
  }

  /**
   * Render a single frame
   */
  render() {
    if (!this.device || !this.pipeline || this.deviceLost) return;

    // Use multi-pass rendering if buffers are active
    if (this.isMultiPassActive()) {
      this.renderMultiPass();
      return;
    }

    // Single-pass rendering (original behavior)
    // Update time (freeze when paused)
    if (this.isPaused) {
      this.uniforms.time = this.pausedTime;
    } else {
      this.uniforms.time = (performance.now() - this.startTime) / 1000;
    }

    // Update camera textures
    if (this.textureManager) {
      this.textureManager.updateAllCameraTextures();
    }

    // Update uniforms
    this.updateUniforms();

    // Ensure bind groups are up to date (only rebuilds if dirty)
    this.ensureBindGroups();

    // Get current texture
    const textureView = this.context.getCurrentTexture().createView();

    // Create command encoder
    const encoder = this.device.createCommandEncoder();

    // Begin render pass
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: textureView,
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: 'clear',
          storeOp: 'store',
        },
      ],
    });

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.uniformBindGroup);
    pass.draw(3); // Fullscreen triangle
    pass.end();

    // Submit commands
    this.device.queue.submit([encoder.finish()]);

    // Post-render: frame callback and FPS update
    this.postRender();
  }

  /**
   * Start the render loop
   */
  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.startTime = performance.now();
    this.lastFpsUpdate = performance.now();
    this.frameCount = 0;

    const loop = () => {
      if (!this.isRunning) return;
      this.render();
      this.animationId = requestAnimationFrame(loop);
    };
    loop();
  }

  /**
   * Stop the render loop
   */
  stop() {
    this.isRunning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Pause time (rendering continues but time is frozen)
   */
  pause() {
    if (this.isPaused) return;
    this.isPaused = true;
    this.pausedTime = (performance.now() - this.startTime) / 1000;
    this.pauseStartTime = performance.now();
  }

  /**
   * Resume time from paused state
   */
  resume() {
    if (!this.isPaused) return;
    // Adjust startTime to account for pause duration
    const pauseDuration = performance.now() - this.pauseStartTime;
    this.startTime += pauseDuration;
    this.isPaused = false;
  }

  /**
   * Toggle pause state
   * @returns {boolean} New pause state
   */
  togglePause() {
    if (this.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
    return this.isPaused;
  }

  /**
   * Update shader and recreate pipeline
   * @param {string} wgslCode - WGSL fragment shader code
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async updateShader(wgslCode) {
    return this.createPipeline(wgslCode);
  }

  /**
   * Mark bind groups as needing rebuild (e.g., when textures change)
   */
  rebuildBindGroup() {
    this.bindGroupsDirty = true;
  }

  /**
   * Ensure all bind groups are up to date (rebuild if dirty)
   */
  ensureBindGroups() {
    if (!this.bindGroupsDirty || !this.device || !this.bindGroupLayout) return;

    this.uniformBindGroup = this.createMainBindGroup();
    this.rebuildAllBufferBindGroups();
    this.bindGroupsDirty = false;
  }

  /**
   * Create the main pass bind group with all texture bindings
   * @returns {GPUBindGroup}
   */
  createMainBindGroup() {
    return this.createBindGroupWithBuffers();
  }

  /**
   * Create bind group for a buffer pass
   * @param {string} bufferId - Buffer ID
   * @returns {GPUBindGroup}
   */
  createBufferBindGroup(bufferId) {
    return this.createBindGroupWithBuffers();
  }

  /**
   * Create bind group with all texture and buffer bindings
   * @returns {GPUBindGroup}
   */
  createBindGroupWithBuffers() {
    return this.device.createBindGroup({
      layout: this.bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: this.uniformBuffer } },
        { binding: 1, resource: this.textureManager.getTextureView(0) },
        { binding: 2, resource: this.textureManager.getSampler() },
        { binding: 3, resource: this.textureManager.getTextureView(1) },
        { binding: 4, resource: this.textureManager.getTextureView(2) },
        { binding: 5, resource: this.textureManager.getTextureView(3) },
        { binding: 6, resource: this.getBufferTextureView(PASS_IDS.BUFFER_A, 'previous') },
        { binding: 7, resource: this.getBufferTextureView(PASS_IDS.BUFFER_B, 'previous') },
        { binding: 8, resource: this.getBufferTextureView(PASS_IDS.BUFFER_C, 'previous') },
        { binding: 9, resource: this.getBufferTextureView(PASS_IDS.BUFFER_D, 'previous') },
      ],
    });
  }

  /**
   * Rebuild all buffer bind groups
   */
  rebuildAllBufferBindGroups() {
    for (const bufferId of this.enabledBuffers) {
      if (this.bufferPipelines.has(bufferId)) {
        this.bufferBindGroups.set(bufferId, this.createBufferBindGroup(bufferId));
      }
    }
  }

  /**
   * Get texture manager
   * @returns {TextureManager}
   */
  getTextureManager() {
    return this.textureManager;
  }

  /**
   * Set enabled buffers for multi-pass rendering
   * @param {string[]} bufferIds - List of buffer IDs in execution order
   */
  setEnabledBuffers(bufferIds) {
    // Find buffers that are no longer enabled and clean them up
    const newBufferSet = new Set(bufferIds);
    for (const oldBufferId of this.enabledBuffers) {
      if (!newBufferSet.has(oldBufferId)) {
        // Clean up disabled buffer
        this.bufferPipelines.delete(oldBufferId);
        this.bufferBindGroups.delete(oldBufferId);
        const textures = this.bufferTextures.get(oldBufferId);
        if (textures) {
          textures.current?.destroy();
          textures.previous?.destroy();
          this.bufferTextures.delete(oldBufferId);
        }
      }
    }

    this.enabledBuffers = [...bufferIds];

    // Create textures for any new buffers
    for (const bufferId of bufferIds) {
      if (!this.bufferTextures.has(bufferId)) {
        this.createBufferTextures(bufferId);
      }
    }

    // Mark bind groups dirty if we need to rebuild main pass bind group
    this.bindGroupsDirty = true;
  }

  /**
   * Create double-buffered textures for a buffer pass
   * @param {string} bufferId - Buffer ID
   */
  createBufferTextures(bufferId) {
    if (!this.device) return;

    const width = Math.max(1, this.canvas.width);
    const height = Math.max(1, this.canvas.height);

    const createTexture = (label) =>
      this.device.createTexture({
        label,
        size: [width, height],
        format: BUFFER_FORMAT,
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.RENDER_ATTACHMENT |
          GPUTextureUsage.COPY_SRC,
      });

    // Destroy existing textures if any
    const existing = this.bufferTextures.get(bufferId);
    if (existing) {
      existing.current?.destroy();
      existing.previous?.destroy();
    }

    const current = createTexture(`${bufferId}-current`);
    const previous = createTexture(`${bufferId}-previous`);

    this.bufferTextures.set(bufferId, {
      current,
      currentView: current.createView(),
      previous,
      previousView: previous.createView(),
    });

    // Mark bind groups as dirty since textures changed
    this.bindGroupsDirty = true;
  }

  /**
   * Get texture view for a buffer (current or previous)
   * @param {string} bufferId - Buffer ID
   * @param {string} which - 'current' or 'previous'
   * @returns {GPUTextureView}
   */
  getBufferTextureView(bufferId, which = 'previous') {
    const textures = this.bufferTextures.get(bufferId);
    if (textures) {
      // Return cached view instead of creating a new one
      return which === 'current' ? textures.currentView : textures.previousView;
    }
    // Return a dummy view if buffer doesn't exist
    return this.textureManager.getDummyTextureView();
  }

  /**
   * Swap current and previous textures for all enabled buffers
   */
  swapBuffers() {
    for (const bufferId of this.enabledBuffers) {
      const textures = this.bufferTextures.get(bufferId);
      if (textures) {
        // Swap textures
        const tempTexture = textures.current;
        textures.current = textures.previous;
        textures.previous = tempTexture;
        // Swap cached views
        const tempView = textures.currentView;
        textures.currentView = textures.previousView;
        textures.previousView = tempView;
      }
    }
    // Mark bind groups as dirty after swap
    this.bindGroupsDirty = true;
  }

  /**
   * Update shader for a specific pass and create its pipeline
   * @param {string} passId - Pass ID ('main', 'bufferA', etc.)
   * @param {string} wgslCode - WGSL fragment shader code
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async updatePassShader(passId, wgslCode) {
    if (passId === PASS_IDS.MAIN) {
      return this.updateShader(wgslCode);
    }

    // Buffer pass
    if (!this.device) {
      return { success: false, error: 'Device not initialized' };
    }

    try {
      const fragEntryPoint = this.findFragmentEntryPoint(wgslCode);
      if (!fragEntryPoint) {
        return { success: false, error: 'No @fragment entry point found in shader' };
      }

      const vertexModule = this.device.createShaderModule({
        label: `${passId} Vertex Shader`,
        code: VERTEX_SHADER,
      });

      const fragmentModule = this.device.createShaderModule({
        label: `${passId} Fragment Shader`,
        code: wgslCode,
      });

      // Check for compilation errors
      const fragmentInfo = await fragmentModule.getCompilationInfo();
      const errors = fragmentInfo.messages.filter((m) => m.type === 'error');

      if (errors.length > 0) {
        const errorMsg = errors.map((e) => `Line ${e.lineNum}: ${e.message}`).join('\n');
        return { success: false, error: `Shader compilation error:\n${errorMsg}` };
      }

      // Create pipeline for buffer pass (renders to rgba16float)
      const pipeline = this.device.createRenderPipeline({
        label: `${passId} Pipeline`,
        layout: this.device.createPipelineLayout({
          bindGroupLayouts: [this.bindGroupLayout],
        }),
        vertex: {
          module: vertexModule,
          entryPoint: 'main',
        },
        fragment: {
          module: fragmentModule,
          entryPoint: fragEntryPoint,
          targets: [{ format: BUFFER_FORMAT }],
        },
        primitive: {
          topology: 'triangle-list',
        },
      });

      this.bufferPipelines.set(passId, pipeline);
      this.bufferBindGroups.set(passId, this.createBufferBindGroup(passId));

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Render all passes (multi-pass rendering)
   */
  renderMultiPass() {
    if (!this.device || this.deviceLost) return;

    // Update time (freeze when paused)
    if (this.isPaused) {
      this.uniforms.time = this.pausedTime;
    } else {
      this.uniforms.time = (performance.now() - this.startTime) / 1000;
    }

    // Update camera textures
    if (this.textureManager) {
      this.textureManager.updateAllCameraTextures();
    }

    // Update uniforms
    this.updateUniforms();

    // Ensure bind groups are up to date (only rebuilds if dirty)
    this.ensureBindGroups();

    // Create command encoder
    const encoder = this.device.createCommandEncoder();

    // Render buffer passes in order (A -> B -> C -> D)
    for (const bufferId of this.enabledBuffers) {
      const pipeline = this.bufferPipelines.get(bufferId);
      if (!pipeline) continue;

      const textures = this.bufferTextures.get(bufferId);
      if (!textures) continue;

      // Use cached bind group
      const bindGroup = this.bufferBindGroups.get(bufferId);
      if (!bindGroup) continue;

      const pass = encoder.beginRenderPass({
        colorAttachments: [
          {
            view: textures.currentView,
            clearValue: { r: 0, g: 0, b: 0, a: 1 },
            loadOp: 'clear',
            storeOp: 'store',
          },
        ],
      });

      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      pass.draw(3);
      pass.end();
    }

    // Render main pass to canvas
    if (this.pipeline) {
      const textureView = this.context.getCurrentTexture().createView();

      const pass = encoder.beginRenderPass({
        colorAttachments: [
          {
            view: textureView,
            clearValue: { r: 0, g: 0, b: 0, a: 1 },
            loadOp: 'clear',
            storeOp: 'store',
          },
        ],
      });

      pass.setPipeline(this.pipeline);
      pass.setBindGroup(0, this.uniformBindGroup);
      pass.draw(3);
      pass.end();
    }

    // Submit commands
    this.device.queue.submit([encoder.finish()]);

    // Swap buffers for next frame (current becomes previous)
    this.swapBuffers();

    // Post-render: frame callback and FPS update
    this.postRender();
  }

  /**
   * Check if multi-pass mode is active
   * @returns {boolean}
   */
  isMultiPassActive() {
    return this.enabledBuffers.length > 0 && this.bufferPipelines.size > 0;
  }

  /**
   * Recreate all buffer textures (e.g., on resize)
   */
  recreateBufferTextures() {
    for (const bufferId of this.enabledBuffers) {
      this.createBufferTextures(bufferId);
    }
    // Rebuild bind groups after texture recreation
    this.rebuildAllBufferBindGroups();
    if (this.pipeline) {
      this.uniformBindGroup = this.createMainBindGroup();
    }
  }

  /**
   * Set frame callback (called after each render)
   * @param {Function} callback
   */
  setOnFrameCallback(callback) {
    this.onFrameCallback = callback;
  }

  /**
   * Report an error
   * @param {string} message
   */
  reportError(message) {
    console.error('WebGPU Error:', message);
    if (this.onError) {
      this.onError(message);
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.stop();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.textureManager) {
      this.textureManager.destroy();
      this.textureManager = null;
    }

    if (this.uniformBuffer) {
      this.uniformBuffer.destroy();
      this.uniformBuffer = null;
    }

    // Clean up buffer textures
    for (const [, textures] of this.bufferTextures) {
      textures.current?.destroy();
      textures.previous?.destroy();
    }
    this.bufferTextures.clear();
    this.bufferPipelines.clear();
    this.bufferBindGroups.clear();
    this.enabledBuffers = [];

    this.pipeline = null;
    this.uniformBindGroup = null;
    this.bindGroupLayout = null;
    this.context = null;
    this.device = null;
  }
}
